import { Box } from '@mui/material';
import { useUpdateChallengeToPatient } from '_api/mutations/challenge';
import { AddChallengeToPatientPayload } from '_api/types';
import { SEVERITY } from '_constants/derived';
import { PrimaryButton, SecondaryButton } from 'components/atoms';
import { AlertMessage, BaseModal } from 'components/molecules';
import { EditChallengeToPatientProfileForm } from 'components/organisms/forms/EditChallengeToPatientProfileForm';
import {
  hasNonblankPatientInstructions,
  normalizePatientInstructions,
} from 'components/organisms/forms/patientInstructions';
import React, { useState } from 'react';

import { EditChallengeToPatientProfileModalProps } from './types';

export const EditChallengeToPatientProfileModal = ({
  open,
  onClose,
  initValue,
  label,
}: EditChallengeToPatientProfileModalProps<AddChallengeToPatientPayload>) => {
  const [errorMessage, setErrorMessage] = useState('');
  const [showSuccess, setShowSuccess] = useState(false);
  const { mutateAsync: updateChallengeToPatient } = useUpdateChallengeToPatient();
  const [payload, setPayload] = useState<AddChallengeToPatientPayload>(initValue);

  const close = () => {
    onClose();
    setPayload(initValue);
  };

  const onUpdate = async (key: any, arg: any) => {
    setPayload((current) => ({
      ...initValue,
      ...current,
      [key]: arg,
    }));
  };

  const onSubmit = () => {
    if (payload.perWeek <= 0) {
      setErrorMessage("'Times Per Week' value must be greater than 0.");
      return;
    }
    if (!hasNonblankPatientInstructions(payload.instructionList)) {
      setErrorMessage('At least one instruction is required.');
      return;
    }
    updateChallengeToPatient({
      ...payload,
      instructionList: normalizePatientInstructions(payload.instructionList),
    })
      .then(() => {
        setShowSuccess(true);
        setTimeout(() => onClose(), 1500);
      })
      .catch((error) => {
        if (error.response && error.response.status === 403) {
          setErrorMessage(
            'Unexpected error occured. Please close the modal and try again.',
          );
        } else if (error.message) {
          setErrorMessage(`ERROR: ${error.message}`);
        } else {
          setErrorMessage('Unexpected error. Please Try again.');
        }
      });
  };

  return (
    <BaseModal open={open} onClose={close} title={`Edit Challenge - ${label}`}>
      <EditChallengeToPatientProfileForm
        initValue={payload}
        onUpdate={onUpdate}
        onSubmit={onSubmit}
      />
      <AlertMessage
        open={showSuccess}
        onClose={() => setShowSuccess(false)}
        message={'Successfully edited the challenge'}
      />
      <AlertMessage
        open={!!errorMessage}
        onClose={() => setErrorMessage('')}
        severity={SEVERITY.error}
        message={errorMessage}
      />
      <Box
        sx={{
          alignItems: 'center',
          display: 'flex',
          padding: 5,
          paddingBottom: 8,
          justifyContent: 'flex-end',
          gap: 2,
          mt: 2,
        }}
      >
        <SecondaryButton variant="outlined" color="primary" onClick={close}>
          Cancel
        </SecondaryButton>
        <PrimaryButton onClick={onSubmit}>Save</PrimaryButton>
      </Box>
    </BaseModal>
  );
};
